import { useState } from "react";
import { fetchController } from "../../utils/FetchController/fetchController";
import { fileUploadFetch } from "../../utils/FetchController/fileUplaodFetch";
import { getHostDomain } from "../../utils/utils";

// Wizard tool_key → Tool.tool_provider (kept in sync with Step5Connect)
const TOOL_KEY_TO_PROVIDER = {
  hubspot: "hubspot",
  zoho: "zoho",
  gohighlevel: "leadconnector",
  google_calendar: "google_calendar",
  twilio_sms: "twilio",
  paypal: "paypal",
  freshdesk: "freshdesk",
  postgres_lookup: "postgres",
  modmed: "modmed",
};

function resolveToolKey(rawKey, crmChoice) {
  return rawKey === "crm_picker" ? crmChoice || rawKey : rawKey;
}

function interpolate(text, values) {
  return (text || "").replace(
    /\{\{\s*([a-zA-Z0-9_]+)(\s*\|\s*comma-list)?\s*\}\}/g,
    (_, key) => {
      const raw = values?.[key];
      if (raw === undefined || raw === null) return "";
      if (Array.isArray(raw)) return raw.join(", ");
      return String(raw);
    }
  );
}

function buildAssistantPayload({
  draft,
  compiledPrompt,
  assistantName,
  bindings,
  webhookId,
  advanced,
}) {
  const { config, fieldValues, outcomeSelections, summaryEmail } = draft;
  const isVoice = draft.channel === "voice";
  const v = config?.voice_defaults || {};
  const b = config?.behavior_defaults || {};

  const attributes = {
    llm_temperature: b.llm_temperature ?? (isVoice ? 0.3 : 0.5),
  };
  if (isVoice) {
    attributes.first_sentence = interpolate(v.first_sentence || "", fieldValues);
    attributes.speaks_first = v.speaks_first || "bot";
    attributes.detect_voicemail = v.detect_voicemail || "hangup";
    attributes.voicemail_message = v.voicemail_message
      ? interpolate(v.voicemail_message, fieldValues)
      : null;
    attributes.max_call_duration = v.max_call_duration ?? 600;
    attributes.use_turn_detector = v.use_turn_detector ?? true;
    attributes.listen_silence = v.listen_silence ?? 30;
    attributes.listen_silence_message = v.listen_silence_message || null;
    attributes.recognizer_confidence = 0.4;
    attributes.interruption_time = 700;
  } else {
    attributes.conversation_timeout = 7200;
  }

  // Resolve contact_syncs / conversation_syncs arrays from outcomes the
  // user actually checked. The runtime reads these arrays at conversation
  // close to know which LinkToolUser ids to push contact / transcript to.
  const contactSyncs = [];
  const conversationSyncs = [];
  (config?.recommended_outcomes || []).forEach((o) => {
    if (!outcomeSelections[o.key] || !o.tool_key) return;
    const toolKey = resolveToolKey(o.tool_key, draft.crmChoice);
    const binding = bindings[toolKey];
    if (!binding?.toolUserId) return;
    if (o.mechanism === "contact_sync") contactSyncs.push(binding.toolUserId);
    if (o.mechanism === "conversation_sync") conversationSyncs.push(binding.toolUserId);
  });
  attributes.contact_syncs = contactSyncs;
  attributes.conversation_syncs = conversationSyncs;
  attributes.use_extracted_contact = contactSyncs.length > 0;

  const summaryOutcome = (config?.recommended_outcomes || []).find(
    (o) => o.mechanism === "summary_email" && outcomeSelections[o.key]
  );
  if (summaryOutcome) {
    attributes.summarize = { is_active: true, email: summaryEmail || null };
  }

  // Realtime assistant type bundles STT + TTS into the LLM call —
  // the runtime treats this differently from the simple/phone path.
  const useRealtime = !!advanced?.realtime && isVoice;
  const assistantType = useRealtime
    ? "realtime_openai"
    : isVoice
    ? "phone"
    : "simple";

  const payload = {
    name: assistantName,
    description: config?.business_summary || "",
    assistant_type: assistantType,
    llm_model: advanced?.llmModel || "gpt-5.4-mini",
    voice: isVoice,
    system_prompt: compiledPrompt,
    use_tools: (config?.recommended_tools || []).length > 0,
    has_human_agent: !!b.has_human_agent,
    attributes,
  };
  if (webhookId) payload.webhook_id = webhookId;
  return payload;
}

export default function useDeploy() {
  const baseUrl = getHostDomain();
  const [deploying, setDeploying] = useState(false);
  const [progress, setProgress] = useState("");

  const linkToolToAssistant = async ({ assistantId, tool, toolUserId, errors }) => {
    if (!tool?.id || !toolUserId) return null;
    return fetchController(
      `${baseUrl}/api/v1/assistant/${assistantId}/${tool.id}/LinkAssistantTool`,
      "POST",
      {
        org_id: null,
        tool_id: tool.id,
        assistant_id: assistantId,
        tool_user_id: toolUserId,
      }
    ).catch((e) => {
      console.warn(`Tool link failed for ${tool.id}:`, e);
      errors.push(`Failed to link tool ${tool.name || tool.id}`);
      return null;
    });
  };

  const createContactSync = async ({ assistantId, toolUserId, fieldsToBeSynced, errors }) => {
    if (!toolUserId) return null;
    return fetchController(
      `${baseUrl}/api/v1/link_assistant_contact_sync`,
      "POST",
      {
        assistant_id: assistantId,
        link_tool_user_id: toolUserId,
        fields_to_be_synced: fieldsToBeSynced || {},
        attributes: {},
      }
    ).catch((e) => {
      console.warn("Contact-sync create failed:", e);
      errors.push("Failed to create contact-sync row");
      return null;
    });
  };

  const resolveToolBindings = async ({
    toolKeys,
    crmChoice,
    toolsList,
    toolBindings,
  }) => {
    const resolved = {};
    await Promise.all(
      toolKeys.map(async (rawKey) => {
        const toolKey = resolveToolKey(rawKey, crmChoice);
        const provider = TOOL_KEY_TO_PROVIDER[toolKey];
        if (!provider) return;
        const tool = toolsList.find((t) => t.tool_provider === provider);
        if (!tool?.id) return;
        let toolUserId = toolBindings?.[toolKey] || null;
        if (!toolUserId) {
          try {
            const res = await fetchController(
              `${baseUrl}/api/v1/tool/${tool.id}/LinkToolUser?size=1`
            );
            toolUserId = res?.data?.items?.[0]?.id || null;
          } catch (e) {
            console.warn(`LinkToolUser fetch failed for ${toolKey}:`, e);
          }
        }
        if (toolUserId) resolved[toolKey] = { tool, toolUserId };
      })
    );
    return resolved;
  };

  const uploadKnowledgeFile = async ({ file, assistantId, errors }) => {
    try {
      const dsName =
        file.name.replace(/\.[^.]+$/, "").slice(0, 80) || "Wizard upload";
      const dsRes = await fetchController(
        `${baseUrl}/api/v1/datasource`,
        "POST",
        {
          ds_type: "pdf",
          name: dsName,
          description: "Uploaded from the Build-Agent Wizard",
          attributes: {},
        }
      );
      const dsId = dsRes?.data?.id;
      if (!dsId) throw new Error("Datasource create returned no id");

      const uniqueFilename = `${file.name}-${Date.now()}`;
      const fileEndpoint =
        `${baseUrl}/api/v1/datasource/${dsId}/file?ds_type=pdf` +
        `&name=${encodeURIComponent(uniqueFilename)}&description=`;
      const formData = new FormData();
      formData.append("datasourcefile_file", file);
      await fileUploadFetch(formData, fileEndpoint);

      await fetchController(
        `${baseUrl}/api/v1/assistant/${assistantId}/data_source/${dsId}`,
        "POST",
        {}
      ).catch((e) => {
        console.warn(`Linking datasource ${dsId} to assistant failed:`, e);
        errors.push(`Failed to attach ${file.name} to the assistant`);
      });

      return dsId;
    } catch (e) {
      console.warn(`Knowledge upload failed for ${file.name}:`, e);
      errors.push(`Failed to upload ${file.name}`);
      return null;
    }
  };

  // Resolve the chat-widget host. Agency subaccounts get their own CDN —
  // mirrors what IframeWidget.js does so the wizard's preview link looks
  // right under white-label.
  const resolveChatHost = () => {
    const stored = window.localStorage.getItem("chatbotDomain");
    if (stored && stored !== "null" && stored !== "undefined") return stored;
    return "cdn.insighto.ai";
  };

  const deploy = async ({
    draft,
    compiledPrompt,
    assistantName,
    toolsList,
    twilio,
    advanced,
  }) => {
    setDeploying(true);
    const errors = [];
    let assistantId = null;
    let widget = null;
    let widgetUrl = null;

    try {
      const isVoice = draft.channel === "voice";
      const config = draft.config;

      // 1. Resolve tool bindings up front — the assistant payload needs
      // them so contact_syncs / conversation_syncs arrays can be set on
      // creation, not via a follow-up PATCH.
      setProgress("Checking your tool connections...");
      const allToolKeys = new Set();
      (config?.recommended_outcomes || [])
        .filter((o) => draft.outcomeSelections[o.key] && o.tool_key)
        .forEach((o) => allToolKeys.add(o.tool_key));
      (config?.recommended_tools || []).forEach((t) => allToolKeys.add(t.tool_key));
      const toolKeyList = [...allToolKeys].filter(
        (k) =>
          k &&
          k !== "crm_picker" &&
          k !== "call_transfer" &&
          k !== "call_disconnect"
      );
      const bindings = await resolveToolBindings({
        toolKeys: toolKeyList,
        crmChoice: draft.crmChoice,
        toolsList,
        toolBindings: draft.toolBindings,
      });

      // 2. Create the Webhook row first (if any outcome opted in) so we
      // can include webhook_id on the assistant create payload.
      let webhookId = null;
      const webhookOutcome = (config?.recommended_outcomes || []).find(
        (o) => o.mechanism === "webhook" && draft.outcomeSelections[o.key]
      );
      if (webhookOutcome && draft.webhookUrl) {
        setProgress("Setting up your webhook...");
        try {
          const wbRes = await fetchController(
            `${baseUrl}/api/v1/outbound_webhook`,
            "POST",
            {
              endpoint: draft.webhookUrl,
              name: `${assistantName} - conversation.closed`,
              enabled: true,
            }
          );
          webhookId = wbRes?.data?.id || null;
        } catch (e) {
          console.warn("Webhook create failed:", e);
          errors.push("Failed to create the webhook row");
        }
      }

      // 3. Create the assistant with everything wired in attributes
      setProgress("Creating your assistant...");
      const assistantPayload = buildAssistantPayload({
        draft,
        compiledPrompt,
        assistantName,
        bindings,
        webhookId,
        advanced,
      });
      const assistantRes = await fetchController(
        `${baseUrl}/api/v1/assistant`,
        "POST",
        assistantPayload
      );
      assistantId = assistantRes?.data?.id;
      if (!assistantId) throw new Error("Assistant create returned no id");

      // 4. Link in-conversation tools + create contact_sync rows
      setProgress("Linking your tools...");
      const toolLinkPromises = (config?.recommended_tools || [])
        .map((rec) => resolveToolKey(rec.tool_key, draft.crmChoice))
        .map((k) => bindings[k])
        .filter(Boolean)
        .map(({ tool, toolUserId }) =>
          linkToolToAssistant({ assistantId, tool, toolUserId, errors })
        );
      const contactSyncPromises = (config?.recommended_contact_syncs || [])
        .map((cs) => {
          const toolKey = resolveToolKey(cs.tool_key, draft.crmChoice);
          const binding = bindings[toolKey];
          if (!binding) return null;
          return createContactSync({
            assistantId,
            toolUserId: binding.toolUserId,
            fieldsToBeSynced: cs.fields_to_be_synced,
            errors,
          });
        })
        .filter(Boolean);
      await Promise.all([...toolLinkPromises, ...contactSyncPromises]);

      // 5. Upload knowledge files sequentially
      const files = draft.knowledgeFiles || [];
      if (files.length > 0) {
        for (let i = 0; i < files.length; i++) {
          setProgress(
            `Uploading knowledge (${i + 1}/${files.length}): ${files[i].name}`
          );
          await uploadKnowledgeFile({ file: files[i], assistantId, errors });
        }
      }

      // 6. Create the widget(s) so the user has something to test.
      // The widget POST handler unconditionally reads
      // `widget.user_opening_messages` — Pydantic defaults it to None
      // when the field is omitted, and `len(None)` blows up. We send
      // empty defaults for the list / dict fields so the existing
      // handler logic finds what it expects (matches what AddWidgetModal
      // does in useFinalSubmit.js).
      const widgetDefaults = {
        user_opening_messages: [],
        action_buttons: [],
        style_params: {},
        description: draft.config?.business_summary || "",
      };
      const host = resolveChatHost();
      let webCallWidget = null;
      let webCallWidgetUrl = null;
      setProgress(isVoice ? "Setting up your phone number..." : "Creating your chat widget...");
      if (isVoice) {
        if (twilio?.twilio_auth_id && twilio?.sid && twilio?.phone_number) {
          widget = await fetchController(
            `${baseUrl}/api/v1/widget`,
            "POST",
            {
              ...widgetDefaults,
              name: assistantName,
              widget_type: "phone",
              assistant_id: assistantId,
              attributes: {
                twilio_auth_id: twilio.twilio_auth_id,
                sid: twilio.sid,
                phone_number: twilio.phone_number,
              },
            }
          ).catch((e) => {
            console.warn("Widget create (voice) failed:", e);
            errors.push("Couldn't bind your phone number — finish in /widgets");
            return null;
          });
        }
        // Also create a browser-based web-call widget so the user has a
        // way to test the agent without picking up the phone.
        setProgress("Creating your in-browser call widget...");
        webCallWidget = await fetchController(
          `${baseUrl}/api/v1/widget`,
          "POST",
          {
            ...widgetDefaults,
            name: `${assistantName} (web call)`,
            widget_type: "web_call",
            assistant_id: assistantId,
            attributes: {},
          }
        ).catch((e) => {
          console.warn("Widget create (web_call) failed:", e);
          // Soft fail — the phone path still works without the web call.
          return null;
        });
        const webCallId = webCallWidget?.data?.id;
        if (webCallId) {
          webCallWidgetUrl = `https://${host}/web-call-iframe.html?widgetId=${webCallId}`;
        }
      } else {
        widget = await fetchController(
          `${baseUrl}/api/v1/widget`,
          "POST",
          {
            ...widgetDefaults,
            name: assistantName,
            widget_type: "chat",
            assistant_id: assistantId,
            attributes: {},
          }
        ).catch((e) => {
          console.warn("Widget create (chat) failed:", e);
          errors.push("Couldn't create the chat widget — finish in /widgets");
          return null;
        });
        const widgetId = widget?.data?.id;
        if (widgetId) {
          widgetUrl = `https://${host}/bot-iframe.html?widgetId=${widgetId}`;
        }
      }

      setProgress("");
      return {
        assistantId,
        assistantName,
        widget: widget?.data,
        widgetId: widget?.data?.id || null,
        widgetUrl,
        webCallWidget: webCallWidget?.data || null,
        webCallWidgetId: webCallWidget?.data?.id || null,
        webCallWidgetUrl,
        chatbotHost: host,
        phoneNumber: isVoice ? twilio?.phone_number : null,
        errors,
      };
    } catch (e) {
      // Fatal — assistant didn't get created or some other show-stopper.
      // Surface what we have so Step 7 can render a degraded state.
      errors.push(e?.response?.data?.detail || e?.message || "Deploy failed");
      return {
        assistantId,
        assistantName,
        widget: widget?.data,
        widgetUrl,
        phoneNumber: null,
        errors,
        fatal: !assistantId,
      };
    } finally {
      setDeploying(false);
    }
  };

  return { deploy, deploying, progress };
}
